import { Fragment, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../app/session";
import { SearchField } from "../components/SearchField";
import { Card } from "../components/ui";
import { ChevronDownIcon } from "../components/icons";
import { useDateFormats, useT, type MessageKey } from "../i18n/index.js";
import type { DateFormats } from "../i18n/formats.js";
import { cn } from "../lib/cn";
import {
  normalise,
  searchSpace,
  type SearchHit,
  type SearchKind,
} from "../lib/search";
import { syncEngine } from "../lib/sync";

/**
 * Поиск (SPEC §4.6).
 *
 * The search runs on the device, over the rows this client has already synced.
 * Not an optimisation — a necessity: in an `e2ee` space the server holds
 * ciphertext and cannot search it (SPEC §3), and a plaintext-only server search
 * would mean two implementations of one feature, which is the duplication this
 * project has already had to undo once. The client holds everything it is
 * allowed to see, so it is also the only place that can rank across lists,
 * wishes, messages and events at the same time.
 */

/** Enough to fill several screens; nobody scrolls past this looking for a word. */
const RESULT_LIMIT = 60;

/** A group longer than this folds, so one noisy kind cannot bury the others. */
const COLLAPSE_AFTER = 5;

const KIND_ORDER: SearchKind[] = ["item", "list", "wish", "event", "message", "topic"];

const KIND_LABEL: Record<SearchKind, MessageKey> = {
  list: "search.kindList",
  item: "search.kindItem",
  wish: "search.kindWish",
  event: "search.kindEvent",
  message: "search.kindMessage",
  topic: "search.kindTopic",
};

/* --------------------------------------------------------------- formatting */

/** Today shows a time, this year a day, anything older carries its year. */
function stamp(iso: string, formats: DateFormats): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";

  const now = new Date();
  const sameDay =
    at.getDate() === now.getDate() &&
    at.getMonth() === now.getMonth() &&
    at.getFullYear() === now.getFullYear();

  if (sameDay) return formats.time.format(at);
  if (at.getFullYear() === now.getFullYear()) return formats.dayMonthShort.format(at);
  return formats.dayMonthYearShort.format(at);
}

/* ---------------------------------------------------------------- highlight */

interface Piece {
  text: string;
  hit: boolean;
}

/**
 * Split a string around the words of the query.
 *
 * Returned as pieces and rendered as elements, never as markup. The text being
 * highlighted is somebody's own list item, and a person who names a task
 * `<img onerror=…>` would otherwise be running it in their partner's browser —
 * `dangerouslySetInnerHTML` here would turn a search box into stored XSS. React
 * escapes every string it renders, so keeping the split in the data and out of
 * the HTML costs nothing and closes the hole by construction.
 */
function split(text: string, query: string): Piece[] {
  const plain: Piece[] = [{ text, hit: false }];

  const tokens = [
    ...new Set(normalise(query).split(/\s+/).filter((token) => token.length > 0)),
    // Longest first, so "кофе кофеварка" marks the whole word and not its stem.
  ].sort((a, b) => b.length - a.length);

  if (tokens.length === 0) return plain;

  // The same folding the search matched with — otherwise "елка" would find
  // «Ёлка» and then mark nothing in it, which reads as a broken result.
  const folded = normalise(text);
  // Folding must not move the offsets. Case and ё/й are one-for-one, but
  // stripping the accent off a Latin letter is not, and a mark that has slid
  // two characters along is worse than no mark at all.
  if (folded.length !== text.length) return plain;

  const pieces: Piece[] = [];
  let buffer = "";
  let i = 0;

  while (i < text.length) {
    const token = tokens.find((candidate) => folded.startsWith(candidate, i));

    if (!token) {
      buffer += text.charAt(i);
      i += 1;
      continue;
    }

    if (buffer) {
      pieces.push({ text: buffer, hit: false });
      buffer = "";
    }
    pieces.push({ text: text.slice(i, i + token.length), hit: true });
    i += token.length;
  }

  if (buffer) pieces.push({ text: buffer, hit: false });
  return pieces.length > 0 ? pieces : plain;
}

function Marked({ text, query }: { text: string; query: string }) {
  const pieces = useMemo(() => split(text, query), [text, query]);

  return (
    <>
      {pieces.map((piece, index) =>
        piece.hit ? (
          <mark
            key={index}
            className="rounded-[3px] bg-accent-soft px-0.5 text-accent-ink"
          >
            {piece.text}
          </mark>
        ) : (
          <Fragment key={index}>{piece.text}</Fragment>
        ),
      )}
    </>
  );
}

/* --------------------------------------------------------------------- page */

export function SearchPage() {
  const t = useT();
  const { session } = useSession();
  const spaceId = session?.spaceId ?? null;

  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<SearchKind[]>([]);

  const snapshot = useSyncExternalStore(syncEngine.subscribe, syncEngine.getSnapshot);

  const trimmed = query.trim();

  const hits = useMemo(() => {
    // An empty box shows nothing rather than everything: a list of the whole
    // space is not a search result, and scrolling it teaches nothing.
    if (!spaceId || trimmed.length === 0) return [];
    return searchSpace(syncEngine.view(spaceId), trimmed, RESULT_LIMIT);
    // The engine notifies on every applied delta; recomputing then is right.
  }, [spaceId, trimmed, snapshot]);

  const groups = useMemo(
    () =>
      KIND_ORDER.map((kind) => ({
        kind,
        hits: hits.filter((hit) => hit.kind === kind),
      })).filter((group) => group.hits.length > 0),
    [hits],
  );

  // A new query is a new set of groups; carrying the old folds over would leave
  // a group open for no reason the person can see.
  useEffect(() => setExpanded([]), [trimmed]);

  return (
    <div className="flex flex-col gap-4 px-4 pt-4 pb-8">
      <header className="px-1">
        <h1 className="font-display text-2xl">{t("search.title")}</h1>
        <p className="mt-0.5 text-[0.875rem] text-muted">{t("search.subtitle")}</p>
      </header>

      <SearchField value={query} onChange={setQuery} autoFocus />

      {trimmed.length === 0 ? (
        <p className="px-1 text-[0.875rem] text-muted">{t("search.hint")}</p>
      ) : groups.length === 0 ? (
        <Card>
          <div className="flex flex-col gap-1 px-4 py-6 text-center">
            <p className="text-[0.9375rem] text-ink-soft">
              {t("search.empty", { query: trimmed })}
            </p>
            <p className="text-[0.8125rem] text-muted">{t("search.emptyHint")}</p>
          </div>
        </Card>
      ) : (
        <div className="flex flex-col gap-5">
          {groups.map((group) => (
            <Group
              key={group.kind}
              kind={group.kind}
              hits={group.hits}
              query={trimmed}
              open={expanded.includes(group.kind)}
              onToggle={() =>
                setExpanded((prev) =>
                  prev.includes(group.kind)
                    ? prev.filter((kind) => kind !== group.kind)
                    : [...prev, group.kind],
                )
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Group({
  kind,
  hits,
  query,
  open,
  onToggle,
}: {
  kind: SearchKind;
  hits: SearchHit[];
  query: string;
  open: boolean;
  onToggle(): void;
}) {
  const t = useT();
  const foldable = hits.length > COLLAPSE_AFTER;
  const shown = foldable && !open ? hits.slice(0, COLLAPSE_AFTER) : hits;
  const hidden = hits.length - shown.length;

  return (
    <section className="flex flex-col gap-2">
      <h2 className="flex items-baseline gap-2 px-1 text-[0.8125rem] font-semibold tracking-wide text-muted uppercase">
        {t(KIND_LABEL[kind])}
        <span className="tabular text-[0.75rem] font-normal normal-case">
          {hits.length}
        </span>
      </h2>

      <ul className="flex flex-col gap-2">
        {shown.map((hit) => (
          <li key={`${hit.kind}:${hit.id}`}>
            <Hit hit={hit} query={query} />
          </li>
        ))}
      </ul>

      {foldable ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="inline-flex min-h-[var(--touch)] items-center gap-1.5 self-start rounded-card px-1 text-[0.875rem] text-accent transition-colors hover:bg-surface-alt"
        >
          {open ? t("search.collapse") : t("search.expand", { count: hidden })}
          <ChevronDownIcon
            className={cn("size-4 transition-transform", open && "rotate-180")}
          />
        </button>
      ) : null}
    </section>
  );
}

function Hit({ hit, query }: { hit: SearchHit; query: string }) {
  const formats = useDateFormats();
  const at = stamp(hit.at, formats);

  return (
    <Link
      to={hit.href}
      className={cn(
        "flex min-h-[var(--touch)] flex-col justify-center gap-0.5",
        "rounded-row bg-surface px-4 py-3",
        "transition-colors hover:bg-surface-alt",
      )}
    >
      <span className="flex items-baseline gap-2">
        <span className="min-w-0 flex-1 truncate text-[0.9375rem] text-ink">
          <Marked text={hit.title} query={query} />
        </span>
        {at ? (
          <time dateTime={hit.at} className="tabular shrink-0 text-[0.75rem] text-muted">
            {at}
          </time>
        ) : null}
      </span>

      {hit.subtitle ? (
        <span className="truncate text-[0.8125rem] text-muted">{hit.subtitle}</span>
      ) : null}

      {hit.snippet ? (
        <span className="line-clamp-2 text-[0.875rem] text-ink-soft">
          <Marked text={hit.snippet} query={query} />
        </span>
      ) : null}
    </Link>
  );
}
